import { useEffect, useState, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Avatar,
    Button,
    TextField,
    IconButton,
    CircularProgress,
    InputAdornment,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Close as CloseIcon,
    Visibility,
    VisibilityOff,
    Edit as EditIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts';
import { userService } from '../services/userService';
import { toast } from 'react-toastify';

import { useNavigate } from 'react-router-dom';
import { getAvatarUrl } from '../../../utils/helpers';

export const ProfileModal = ({ open, onClose }) => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const [tab, setTab] = useState('general');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        fullName: '',
        phone: '',
        email: '',
    });
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    // Mặc định ẩn số dư
    const [showBalance, setShowBalance] = useState(false);
    const [emailVerifying, setEmailVerifying] = useState(false);
    const [otp, setOtp] = useState('');
    const [sendingOtp, setSendingOtp] = useState(false);
    const [verifyingOtp, setVerifyingOtp] = useState(false);

    // State cho ẩn/hiện mật khẩu
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const hasLoadedProfile = useRef(false);

    const avatarUrl = getAvatarUrl(user?.avatar);

    const handleAvatarUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setSaving(true);
            const res = await userService.uploadAvatar(file);
            const updatedUser = res.data || res.user || res;
            updateUser(updatedUser);
            toast.success('Cập nhật ảnh đại diện thành công');
        } catch (err) {
            const msg = err.response?.data?.message || 'Tải ảnh đại diện thất bại';
            toast.error(msg);
        } finally {
            setSaving(false);
            event.target.value = '';
        }
    };

    useEffect(() => {
        if (!open) {
            hasLoadedProfile.current = false;
            return;
        }

        if (hasLoadedProfile.current) return;

        const fetchProfile = async () => {
            try {
                setLoading(true);
                const res = await userService.getProfile();
                const userData = res.data || res.user || res;

                setForm({
                    fullName: userData.fullName || '',
                    phone: userData.phone || '',
                    email: userData.email || '',
                });

                hasLoadedProfile.current = true;
            } catch (err) {
                if (user) {
                    setForm({
                        fullName: user.fullName || '',
                        phone: user.phone || '',
                        email: user.email || '',
                    });
                }
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [open, user]);

    useEffect(() => {
        if (user && !loading) {
            setForm((prev) => ({
                fullName: user.fullName || prev.fullName,
                phone: user.phone || prev.phone,
                email: user.email || prev.email,
            }));
        }
    }, [user, loading]);

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const res = await userService.updateProfile({
                fullName: form.fullName,
                phone: form.phone,
            });
            const updatedUser = res.data || res.user || res;
            updateUser(updatedUser);
            toast.success('Cập nhật hồ sơ thành công');
            onClose();
        } catch (err) {
            const msg = err.response?.data?.message || 'Cập nhật hồ sơ thất bại';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            toast.error('Mật khẩu xác nhận không khớp');
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            toast.error('Mật khẩu mới phải có ít nhất 6 ký tự');
            return;
        }

        try {
            setSaving(true);

            await userService.changePassword({
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
                confirmPassword: passwordForm.confirmPassword,
            });

            toast.success('Đổi mật khẩu thành công');
            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setTab('general');
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message || 'Đổi mật khẩu thất bại';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    if (!user) return null;

    const initials =
        user.fullName?.split(' ').map((n) => n[0]).join('').toUpperCase() ||
        user.username?.charAt(0).toUpperCase() ||
        'U';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    // Background với gradient xanh nhẹ
                    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0C4A6E 100%)',
                    border: '1px solid rgba(56, 189, 248, 0.2)',
                    boxShadow: '0 20px 60px rgba(14, 165, 233, 0.15)',
                },
            }}
        >
            <DialogContent sx={{ p: 4 }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                        <Typography variant="h5" fontWeight={700} color="#F9FAFB">
                            Cài đặt hồ sơ
                        </Typography>
                        <Typography variant="body2" color="#94A3B8">
                            Quản lý thông tin tài khoản và bảo mật
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        sx={{
                            color: '#94A3B8',
                            '&:hover': { color: '#38BDF8', bgcolor: 'rgba(56, 189, 248, 0.1)' }
                        }}
                        onClick={onClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Tabs */}
                <Box
                    sx={{
                        display: 'flex',
                        bgcolor: 'rgba(15, 23, 42, 0.8)',
                        borderRadius: 999,
                        p: 0.5,
                        mb: 3,
                        border: '1px solid rgba(56, 189, 248, 0.1)',
                    }}
                >
                    <Button
                        onClick={() => setTab('general')}
                        sx={{
                            flex: 1,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: tab === 'general' ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                            color: tab === 'general' ? '#38BDF8' : '#94A3B8',
                            border: tab === 'general' ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid transparent',
                            '&:hover': {
                                bgcolor: tab === 'general' ? 'rgba(56, 189, 248, 0.2)' : 'rgba(148, 163, 184, 0.1)',
                            },
                        }}
                    >
                        Thông tin chung
                    </Button>
                    <Button
                        onClick={() => setTab('security')}
                        sx={{
                            flex: 1,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: tab === 'security' ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                            color: tab === 'security' ? '#38BDF8' : '#94A3B8',
                            border: tab === 'security' ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid transparent',
                            '&:hover': {
                                bgcolor: tab === 'security' ? 'rgba(56, 189, 248, 0.2)' : 'rgba(148, 163, 184, 0.1)',
                            },
                        }}
                    >
                        Bảo mật
                    </Button>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress sx={{ color: '#38BDF8' }} />
                    </Box>
                ) : tab === 'general' ? (
                    <>
                        {/* Số dư tài khoản card */}
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 1.5,
                                background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(56, 189, 248, 0.05) 100%)',
                                border: '1px solid rgba(56, 189, 248, 0.2)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Box>
                                <Typography variant="caption" color="#94A3B8" fontWeight={500}>
                                    Số dư tài khoản
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                                    <Typography 
                                        variant="h4" 
                                        fontWeight={700} 
                                        color="#38BDF8"
                                        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' } }}
                                    >
                                        {showBalance
                                            ? user.wallet?.balance
                                                ? `${parseFloat(user.wallet.balance).toLocaleString('vi-VN')} ${user.wallet.currency || 'VND'}`
                                                : '0 VND'
                                            : '••••••••'}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => setShowBalance((prev) => !prev)}
                                        sx={{
                                            color: '#94A3B8',
                                            '&:hover': { color: '#38BDF8' }
                                        }}
                                    >
                                        {showBalance ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </Box>
                            </Box>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    onClose();
                                    navigate('/deposit');
                                }}
                                sx={{
                                    textTransform: 'none',
                                    background: 'linear-gradient(90deg, #0EA5E9, #38BDF8)',
                                    fontWeight: 600,
                                    boxShadow: '0 4px 14px rgba(14, 165, 233, 0.4)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #0284C7, #0EA5E9)',
                                        boxShadow: '0 6px 20px rgba(14, 165, 233, 0.6)',
                                    },
                                }}
                            >
                                Nạp ngay
                            </Button>
                        </Box>

                        {/* Avatar - Row riêng */}
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(15, 23, 42, 0.6)',
                                border: '1px solid rgba(56, 189, 248, 0.1)',
                            }}
                        >
                            <Typography variant="body2" fontWeight={600} mb={2} color="#E5E7EB">
                                Ảnh đại diện
                            </Typography>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Avatar
                                    src={avatarUrl}
                                    alt={user.fullName}
                                    sx={{
                                        width: { xs: 60, sm: 72 },
                                        height: { xs: 60, sm: 72 },
                                        border: '3px solid rgba(56, 189, 248, 0.3)',
                                        bgcolor: '#1E293B',
                                        color: '#38BDF8',
                                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                        fontWeight: 700,
                                    }}
                                >
                                    {initials}
                                </Avatar>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<EditIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        borderColor: '#38BDF8',
                                        color: '#38BDF8',
                                        fontWeight: 600,
                                        '&:hover': {
                                            borderColor: '#0EA5E9',
                                            bgcolor: 'rgba(14, 165, 233, 0.1)',
                                        },
                                    }}
                                >
                                    Đổi ảnh
                                    <input
                                        hidden
                                        accept="image/*"
                                        type="file"
                                        onChange={handleAvatarUpload}
                                        disabled={saving}
                                    />
                                </Button>
                            </Box>
                        </Box>

                        {/* Họ và tên - Row riêng */}
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(15, 23, 42, 0.6)',
                                border: '1px solid rgba(56, 189, 248, 0.1)',
                            }}
                        >
                            <Typography variant="body2" fontWeight={600} mb={1.5} color="#E5E7EB">
                                Họ và tên
                            </Typography>
                            <TextField
                                fullWidth
                                name="fullName"
                                value={form.fullName}
                                onChange={handleChange}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        bgcolor: 'rgba(15, 23, 42, 0.8)',
                                        color: '#F9FAFB',
                                        border: '1px solid rgba(56, 189, 248, 0.2)',
                                        borderRadius: 2,
                                        '&:hover': {
                                            borderColor: 'rgba(56, 189, 248, 0.4)',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: '#38BDF8',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Số điện thoại - Row riêng */}
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(15, 23, 42, 0.6)',
                                border: '1px solid rgba(56, 189, 248, 0.1)',
                            }}
                        >
                            <Typography variant="body2" fontWeight={600} mb={1.5} color="#E5E7EB">
                                Số điện thoại
                            </Typography>
                            <TextField
                                fullWidth
                                name="phone"
                                value={form.phone}
                                onChange={handleChange}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        bgcolor: 'rgba(15, 23, 42, 0.8)',
                                        color: '#F9FAFB',
                                        border: '1px solid rgba(56, 189, 248, 0.2)',
                                        borderRadius: 2,
                                        '&:hover': {
                                            borderColor: 'rgba(56, 189, 248, 0.4)',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: '#38BDF8',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Email - Row riêng */}
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(15, 23, 42, 0.6)',
                                border: '1px solid rgba(56, 189, 248, 0.1)',
                            }}
                        >
                            <Typography variant="body2" fontWeight={600} mb={1.5} color="#E5E7EB">
                                Email
                            </Typography>
                            <Box display="flex" gap={1.5} mb={emailVerifying ? 2 : 0}>
                                <TextField
                                    fullWidth
                                    value={form.email}
                                    disabled
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            bgcolor: 'rgba(15, 23, 42, 0.5)',
                                            color: '#94A3B8',
                                            border: '1px solid rgba(148, 163, 184, 0.2)',
                                            borderRadius: 2,
                                        },
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    disabled={user?.isEmailVerified || sendingOtp}
                                    onClick={async () => {
                                        if (!form.email || user?.isEmailVerified) return;
                                        try {
                                            setSendingOtp(true);
                                            await userService.sendEmailVerification();
                                            setEmailVerifying(true);
                                            toast.success('Đã gửi mã OTP tới email của bạn');
                                        } catch (err) {
                                            const msg = err.response?.data?.message || 'Gửi OTP thất bại';
                                            toast.error(msg);
                                        } finally {
                                            setSendingOtp(false);
                                        }
                                    }}
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        minWidth: '140px',

                                        borderColor: user?.isEmailVerified ? '#10B981' : '#38BDF8',
                                        color: user?.isEmailVerified ? '#10B981' : '#38BDF8',
                                        '&:hover': {
                                            borderColor: user?.isEmailVerified ? '#059669' : '#0EA5E9',
                                            bgcolor: user?.isEmailVerified ? 'rgba(16, 185, 129, 0.1)' : 'rgba(14, 165, 233, 0.1)',
                                        },
                                        '&.Mui-disabled': {
                                            borderColor: user?.isEmailVerified ? '#10B981' : 'rgba(148, 163, 184, 0.3)',
                                            color: user?.isEmailVerified ? '#10B981' : 'rgba(148, 163, 184, 0.5)',
                                        },
                                    }}
                                >
                                    {user?.isEmailVerified
                                        ? 'Đã xác thực'
                                        : sendingOtp
                                            ? 'Đang gửi...'
                                            : 'Xác thực'}
                                </Button>
                            </Box>

                            {emailVerifying && !user?.isEmailVerified && (
                                <Box mt={2} display="flex" gap={1.5}>
                                    <TextField
                                        placeholder="Nhập mã OTP"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        sx={{
                                            flex: 1,
                                            '& .MuiInputBase-root': {
                                                bgcolor: 'rgba(15, 23, 42, 0.8)',
                                                color: '#F9FAFB',
                                                border: '1px solid rgba(56, 189, 248, 0.2)',
                                                borderRadius: 1.5,
                                            },
                                        }}
                                    />
                                    <Button
                                        variant="contained"
                                        disabled={verifyingOtp || !otp}
                                        onClick={async () => {
                                            try {
                                                setVerifyingOtp(true);
                                                const res = await userService.verifyEmail({ email: form.email, otp });
                                                const updatedUser = res.data || res.user || res;
                                                updateUser({
                                                    ...user,
                                                    isEmailVerified: true,
                                                    ...updatedUser
                                                });
                                                toast.success('Xác thực email thành công');
                                                setEmailVerifying(false);
                                                setOtp('');
                                            } catch (err) {
                                                const msg = err.response?.data?.message || 'Xác thực email thất bại';
                                                toast.error(msg);
                                            } finally {
                                                setVerifyingOtp(false);
                                            }
                                        }}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            minWidth: '140px',
                                            background: 'linear-gradient(90deg, #0EA5E9, #38BDF8)',
                                            '&:hover': {
                                                background: 'linear-gradient(90deg, #0284C7, #0EA5E9)',
                                            },
                                        }}
                                    >
                                        {verifyingOtp ? 'Đang xác minh...' : 'Xác nhận'}
                                    </Button>
                                </Box>
                            )}

                            {/* {user?.isEmailVerified && (
                                <Box
                                    display="inline-flex"
                                    alignItems="center"
                                    gap={1}
                                    mt={2}
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        borderRadius: 999,
                                        bgcolor: 'rgba(16, 185, 129, 0.15)',
                                        border: '1px solid rgba(16, 185, 129, 0.3)',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: '#10B981',
                                        }}
                                    />
                                    <Typography variant="body2" color="#10B981" fontWeight={600}>
                                        Email đã được xác thực
                                    </Typography>
                                </Box>
                            )} */}
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving}
                            sx={{
                                py: { xs: 1, sm: 1.5 },
                                background: 'linear-gradient(90deg, #0EA5E9, #38BDF8)',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                                boxShadow: '0 10px 30px rgba(14, 165, 233, 0.4)',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #0284C7, #0EA5E9)',
                                    boxShadow: '0 15px 40px rgba(14, 165, 233, 0.5)',
                                },
                            }}
                        >
                            {saving ? 'Đang lưu...' : 'Lưu thay đổi'}
                        </Button>
                    </>
                ) : (
                    // Tab Bảo mật - Mỗi field một row riêng
                    <Box component="form" onSubmit={handlePasswordChange}>
                        {/* Mật khẩu hiện tại */}
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(15, 23, 42, 0.6)',
                                border: '1px solid rgba(56, 189, 248, 0.1)',
                            }}
                        >
                            <Typography variant="body2" fontWeight={600} mb={1.5} color="#E5E7EB">
                                Mật khẩu hiện tại
                            </Typography>
                            <TextField
                                fullWidth
                                type={showPasswords.current ? 'text' : 'password'}
                                value={passwordForm.currentPassword}
                                onChange={(e) =>
                                    setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                                                edge="end"
                                                sx={{ color: '#94A3B8' }}
                                            >
                                                {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        bgcolor: 'rgba(15, 23, 42, 0.8)',
                                        color: '#F9FAFB',
                                        border: '1px solid rgba(56, 189, 248, 0.2)',
                                        borderRadius: 2,
                                        '&:hover': {
                                            borderColor: 'rgba(56, 189, 248, 0.4)',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: '#38BDF8',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Mật khẩu mới */}
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(15, 23, 42, 0.6)',
                                border: '1px solid rgba(56, 189, 248, 0.1)',
                            }}
                        >
                            <Typography variant="body2" fontWeight={600} mb={1.5} color="#E5E7EB">
                                Mật khẩu mới
                            </Typography>
                            <TextField
                                fullWidth
                                type={showPasswords.new ? 'text' : 'password'}
                                value={passwordForm.newPassword}
                                onChange={(e) =>
                                    setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                                                edge="end"
                                                sx={{ color: '#94A3B8' }}
                                            >
                                                {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        bgcolor: 'rgba(15, 23, 42, 0.8)',
                                        color: '#F9FAFB',
                                        border: '1px solid rgba(56, 189, 248, 0.2)',
                                        borderRadius: 2,
                                        '&:hover': {
                                            borderColor: 'rgba(56, 189, 248, 0.4)',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: '#38BDF8',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        {/* Xác nhận mật khẩu mới */}
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(15, 23, 42, 0.6)',
                                border: '1px solid rgba(56, 189, 248, 0.1)',
                            }}
                        >
                            <Typography variant="body2" fontWeight={600} mb={1.5} color="#E5E7EB">
                                Xác nhận mật khẩu mới
                            </Typography>
                            <TextField
                                fullWidth
                                type={showPasswords.confirm ? 'text' : 'password'}
                                value={passwordForm.confirmPassword}
                                onChange={(e) =>
                                    setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                                                edge="end"
                                                sx={{ color: '#94A3B8' }}
                                            >
                                                {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        bgcolor: 'rgba(15, 23, 42, 0.8)',
                                        color: '#F9FAFB',
                                        border: '1px solid rgba(56, 189, 248, 0.2)',
                                        borderRadius: 2,
                                        '&:hover': {
                                            borderColor: 'rgba(56, 189, 248, 0.4)',
                                        },
                                        '&.Mui-focused': {
                                            borderColor: '#38BDF8',
                                        },
                                    },
                                }}
                            />
                        </Box>

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={saving}
                            sx={{
                                py: 1.5,
                                background: 'linear-gradient(90deg, #0EA5E9, #38BDF8)',
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: '1rem',
                                boxShadow: '0 10px 30px rgba(14, 165, 233, 0.4)',
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #0284C7, #0EA5E9)',
                                    boxShadow: '0 15px 40px rgba(14, 165, 233, 0.5)',
                                },
                            }}
                        >
                            {saving ? 'Đang đổi...' : 'Đổi mật khẩu'}
                        </Button>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};